/* eslint-disable */
import { createContext, useState } from 'react'
import { ThemeProvider, createTheme, Theme } from '@mui/material/styles'
import { CssBaseline } from '@mui/material'
import {
    OSMO,
    FONT_FAMILY_PRIMARY,
    FONT_FAMILY_MONO,
    RADIUS,
    SHADOW_SOFT,
} from '../config/brandTokens'

export const ThemeContext = createContext({})
export const ThemeSelector = createContext(null)

interface DefaultState {
    theme: Theme
}

export const ThemeBuilder = ({ children }) => {
    const defaultTheme = createTheme({
        palette: {
            mode: 'dark',
            primary: {
                light: OSMO.green,
                main: OSMO.green,
                dark: OSMO.greenDark,
                // Green is luminous — dark text reads best on it (matches logo).
                contrastText: OSMO.black,
            },
            secondary: {
                light: OSMO.teal,
                main: OSMO.teal,
                dark: OSMO.tealDark,
                contrastText: OSMO.white,
            },
            success: { main: OSMO.green, contrastText: OSMO.black },
            info: { main: OSMO.teal, contrastText: OSMO.white },
            action: {
                hover: OSMO.greenSoft,
                active: OSMO.green,
            },
            background: {
                paper: OSMO.surface,
                default: OSMO.black,
            },
            text: {
                primary: OSMO.white,
                secondary: 'rgba(255, 255, 255, 0.70)',
            },
            divider: OSMO.border,
        },
        shape: {
            borderRadius: RADIUS,
        },
        typography: {
            fontFamily: FONT_FAMILY_PRIMARY,
            // Inter Bold for headings/titles per brand guidelines.
            h1: { fontWeight: 700 },
            h2: { fontWeight: 700 },
            h3: { fontWeight: 700 },
            h4: { fontWeight: 700 },
            h5: { fontWeight: 700 },
            h6: { fontWeight: 700 },
            button: { textTransform: 'none', fontWeight: 600 },
            // Oxygen Mono for fine detail text/accents.
            overline: {
                fontFamily: FONT_FAMILY_MONO,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
            },
            caption: { fontFamily: FONT_FAMILY_MONO },
        },
        components: {
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: { borderRadius: RADIUS, fontWeight: 600 },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                        border: `1px solid ${OSMO.border}`,
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                        backgroundColor: OSMO.black,
                        borderBottom: `1px solid ${OSMO.border}`,
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        backgroundColor: OSMO.surface,
                        border: `1px solid ${OSMO.border}`,
                        fontFamily: FONT_FAMILY_MONO,
                        fontSize: 12,
                    },
                },
            },
            // Visible keyboard focus ring across all clickable surfaces.
            MuiButtonBase: {
                styleOverrides: {
                    root: {
                        '&.Mui-focusVisible': {
                            outline: `2px solid ${OSMO.green}`,
                            outlineOffset: 2,
                        },
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                        border: `1px solid ${OSMO.border}`,
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        // Subtle filled surface so fields read as modern input
                        // chips on the dark UI, with a smooth focus transition.
                        backgroundColor: 'rgba(255,255,255,0.025)',
                        transition:
                            'background-color 0.15s ease, border-color 0.15s ease',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: OSMO.border,
                        },
                        '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.045)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.28)',
                        },
                        '&.Mui-focused': {
                            backgroundColor: 'rgba(255,255,255,0.045)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: OSMO.green,
                            borderWidth: 1,
                            boxShadow: `0 0 0 3px ${OSMO.greenSoft}`,
                        },
                        '&.Mui-disabled': {
                            backgroundColor: 'rgba(255,255,255,0.015)',
                        },
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        '&.Mui-focused': { color: OSMO.green },
                    },
                },
            },
            MuiFormHelperText: {
                styleOverrides: {
                    root: { marginLeft: 2, fontSize: 11 },
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    paper: {
                        backgroundColor: OSMO.surface,
                        backgroundImage: 'none',
                        border: `1px solid ${OSMO.border}`,
                        borderRadius: RADIUS,
                        boxShadow: SHADOW_SOFT,
                    },
                    option: {
                        '&[aria-selected="true"]': {
                            backgroundColor: OSMO.greenSoft,
                        },
                        '&.Mui-focused': {
                            backgroundColor: 'rgba(255,255,255,0.06)',
                        },
                    },
                },
            },
            MuiAccordion: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                        border: `1px solid ${OSMO.border}`,
                        borderRadius: RADIUS,
                        overflow: 'hidden',
                        // Drop the default top hairline pseudo-divider.
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': { margin: 0 },
                    },
                },
            },
            MuiAccordionSummary: {
                styleOverrides: {
                    root: {
                        minHeight: 52,
                        '&.Mui-expanded': { minHeight: 52 },
                        '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.03)',
                        },
                    },
                    content: { '&.Mui-expanded': { margin: '12px 0' } },
                    expandIconWrapper: {
                        color: 'rgba(255,255,255,0.55)',
                        '&.Mui-expanded': { color: OSMO.green },
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: { fontWeight: 600 },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        backgroundImage: 'none',
                        border: `1px solid ${OSMO.border}`,
                        borderRadius: RADIUS + 2,
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: { borderColor: OSMO.border },
                    head: {
                        fontFamily: FONT_FAMILY_MONO,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        fontSize: 11,
                        color: 'rgba(255,255,255,0.70)',
                    },
                },
            },
            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        '&.Mui-selected': {
                            backgroundColor: OSMO.greenSoft,
                            color: OSMO.green,
                            '&:hover': { backgroundColor: OSMO.greenSoft },
                        },
                    },
                },
            },
            MuiMenu: {
                styleOverrides: {
                    paper: { backgroundImage: 'none' },
                },
            },
            MuiTabs: {
                styleOverrides: {
                    indicator: { backgroundColor: OSMO.green },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        fontWeight: 600,
                        '&.Mui-selected': { color: OSMO.green },
                    },
                },
            },
            MuiLink: {
                styleOverrides: {
                    root: { color: OSMO.green, textDecorationColor: OSMO.green },
                },
            },
        },
    })
    const defaultState: DefaultState = {
        theme: defaultTheme,
    }
    const [state, setNewState] = useState(defaultState)
    const { theme } = state

    return (
        <ThemeContext.Provider value={theme}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </ThemeContext.Provider>
    )
}
